/**
 * useFokusFilters - Filter state management for FokusButton
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { matchesDueDateFilter, getTaskCategories, getTaskTags } from '../../filterUtils';

export function useFokusFilters({
  tasks = [],
  categories = [],
  statuses = [],
  allTags = [],
  isFocusMode,
  onFilterFocus,
}) {
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [selectedDueDates, setSelectedDueDates] = useState(new Set());
  const [selectedPriorities, setSelectedPriorities] = useState(new Set());

  // Derived data
  const taskCategories = useMemo(() => getTaskCategories(tasks, categories), [tasks, categories]);
  const taskTags = useMemo(() => getTaskTags(tasks, allTags), [tasks, allTags]);

  // Get status options
  const statusOptions = useMemo(() => {
    if (statuses && statuses.length > 0) return statuses;
    return [];
  }, [statuses]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return selectedCategories.size + selectedStatuses.size + selectedTags.size + 
           selectedDueDates.size + selectedPriorities.size;
  }, [selectedCategories, selectedStatuses, selectedTags, selectedDueDates, selectedPriorities]);

  // Get matching task IDs based on current filter selections
  const matchingTaskIds = useMemo(() => {
    if (activeFilterCount === 0) return [];
    
    return tasks.filter(task => {
      // Category filter
      if (selectedCategories.size > 0) {
        const taskCategory = task.category || task.tags?.[0];
        if (!taskCategory || !selectedCategories.has(taskCategory)) return false;
      }
      // Status filter
      if (selectedStatuses.size > 0 && !selectedStatuses.has(task.status)) return false;
      // Tag filter
      if (selectedTags.size > 0 && (!task.tags || !task.tags.some(t => selectedTags.has(t)))) return false;
      // Due date filter
      if (selectedDueDates.size > 0 && !Array.from(selectedDueDates).some(id => matchesDueDateFilter(task.dueDate, id))) return false;
      // Priority filter
      if (selectedPriorities.size > 0 && !selectedPriorities.has(task.priority || 'none')) return false;
      return true;
    }).map(t => t.id);
  }, [tasks, selectedCategories, selectedStatuses, selectedTags, selectedDueDates, selectedPriorities, activeFilterCount]);

  // Auto-fokus when filter selections change (live filtering)
  const prevFocusModeRef = useRef(isFocusMode);
  
  useEffect(() => {
    // If we just exited focus mode, don't re-trigger
    if (prevFocusModeRef.current && !isFocusMode) {
      prevFocusModeRef.current = isFocusMode;
      return;
    }
    prevFocusModeRef.current = isFocusMode;
    
    // Only auto-apply when we have filters and we're not already in focus mode
    if (matchingTaskIds.length > 0 && activeFilterCount > 0 && !isFocusMode) {
      onFilterFocus(matchingTaskIds);
    }
  }, [matchingTaskIds, activeFilterCount, onFilterFocus, isFocusMode]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setSelectedCategories(new Set());
    setSelectedStatuses(new Set());
    setSelectedTags(new Set());
    setSelectedDueDates(new Set());
    setSelectedPriorities(new Set());
  }, []);

  // Toggle functions
  const toggleCategory = (name) => setSelectedCategories(prev => { 
    const next = new Set(prev); 
    next.has(name) ? next.delete(name) : next.add(name); 
    return next; 
  });
  
  const toggleStatus = (id) => setSelectedStatuses(prev => { 
    const next = new Set(prev); 
    next.has(id) ? next.delete(id) : next.add(id); 
    return next; 
  });
  
  const toggleTag = (name) => setSelectedTags(prev => { 
    const next = new Set(prev); 
    next.has(name) ? next.delete(name) : next.add(name); 
    return next; 
  });
  
  const toggleDueDate = (id) => setSelectedDueDates(prev => { 
    const next = new Set(prev); 
    next.has(id) ? next.delete(id) : next.add(id); 
    return next; 
  });
  
  const togglePriority = (id) => setSelectedPriorities(prev => { 
    const next = new Set(prev); 
    next.has(id) ? next.delete(id) : next.add(id); 
    return next; 
  });

  return {
    // State
    selectedCategories,
    selectedStatuses,
    selectedTags,
    selectedDueDates,
    selectedPriorities,
    // Derived
    taskCategories,
    taskTags,
    statusOptions,
    activeFilterCount,
    matchingTaskIds,
    // Actions
    clearAllFilters,
    toggleCategory,
    toggleStatus,
    toggleTag,
    toggleDueDate,
    togglePriority,
  };
}

export default useFokusFilters;
